import { ApiResponse } from '@/models';
import { CreateOrganizationRequest, Organization } from '@/models/organization.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface PaginatedOrganizationsResponse {
  content: Organization[];
  totalElements: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  private apiUrl = `${environment.apiUrl}/admin`;

  constructor(private http: HttpClient) {}

  /**
   * Get all organizations
   * @returns {Observable<Organization[]>} - Observable containing the organizations data
   */
  getOrganizations(): Observable<Organization[]> {
    return this.http.get<Organization[]>(`${this.apiUrl}/orgaos/all`);
  }

  /**
   * Get paginated organizations with optional search
   * @param params - Optional parameters for filtering and pagination
   * @returns {Observable<PaginatedOrganizationsResponse>} - Observable containing the paginated organizations data
   */
  getPaginatedOrganizations(params?: {
    nome?: string;
    page?: number;
    size?: number;
  }): Observable<PaginatedOrganizationsResponse> {
    let httpParams = new HttpParams();

    if (params?.nome) {
      httpParams = httpParams.set('nome', params.nome);
    }
    if (params?.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params?.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }

    return this.http.get<PaginatedOrganizationsResponse>(`${this.apiUrl}/orgaos`, { params: httpParams });
  }

  /**
   * Get organization by ID
   * @param id - Organization ID
   * @returns {Observable<Organization>} - Observable containing the organization data
   */
  getOrganizationById(id: number): Observable<Organization> {
    return this.http.get<Organization>(`${this.apiUrl}/orgaos/${id}`);
  }

  /**
   * Create a new organization
   * @param organizationData - Data for the new organization
   * @returns {Observable<Organization>} - Observable containing the created organization data
   */
  createOrganization(organizationData: CreateOrganizationRequest): Observable<Organization> {
    return this.http.post<Organization>(`${this.apiUrl}/orgaos`, organizationData);
  }

  /**
   * Update an existing organization
   * @param id - Organization ID
   * @param organizationData - Updated organization data
   * @returns {Observable<Organization>} - Observable containing the updated organization data
   */
  updateOrganization(id: number, organizationData: CreateOrganizationRequest): Observable<Organization> {
    return this.http.put<Organization>(`${this.apiUrl}/orgaos/${id}`, organizationData);
  }

  /**
   * Save basic organization information (Step 1)
   * @param basicInfo - Basic organization data
   * @param orgaoId - Optional organization ID for updating
   * @returns {Observable<Organization>} - Observable containing the organization data with ID
   */
  saveBasicInfo(basicInfo: {
    nome: string;
    cnpj: string;
    emailContato: string;
    telefoneContato: string;
  }, orgaoId?: number): Observable<Organization> {
    let httpParams = new HttpParams();
    if (orgaoId) {
      httpParams = httpParams.set('orgaoId', orgaoId.toString());
    }

    return this.http.post<Organization>(`${this.apiUrl}/orgaos/basic-info`, basicInfo, { params: httpParams });
  }

  /**
   * Save organization address (Step 2)
   * @param orgaoId - Organization ID
   * @param addressData - Address data
   * @returns {Observable<any>} - Observable containing the response
   */
  saveAddress(orgaoId: number, addressData: {
    cep: string;
    logradouro: string;
    numero: string;
    semNumero: boolean;
    complemento?: string;
    bairro: string;
    cidadeId: number;
    cidadeNome: string;
    estadoUf: string;
    estadoNome: string;
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/orgaos/${orgaoId}/endereco`, addressData);
  }

  /**
   * Save bank details (Step 3)
   * @param orgaoId - Organization ID
   * @param bankData - Bank details data
   * @returns {Observable<any>} - Observable containing the response
   */
  saveBankDetails(orgaoId: number, bankData: {
    bancoCodigo: string;
    bancoNome: string;
    agencia: string;
    agenciaDigito?: string;
    conta: string;
    contaDigito?: string;
    convenio: string;
    carteira: string;
    variacaoCarteira: string;
    nossoNumeroPrefixo?: string;
    beneficiarioNome?: string;
    beneficiarioDocumento?: string;
    ativo: boolean;
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/orgaos/${orgaoId}/bank-details`, bankData);
  }

  /**
   * Save bank API credentials (Step 4 - Optional)
   * @param orgaoId - Organization ID
   * @param credentialsData - API credentials data (productionApplicationKey is optional for Bradesco)
   * @returns {Observable<any>} - Observable containing the response
   */
  saveBankApiCredentials(orgaoId: number, credentialsData: {
    productionApplicationKey?: string;
    clientId: string;
    clientSecret: string;
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/orgaos/${orgaoId}/bank-api-credentials`, credentialsData);
  }

  /**
   * Save bank mTLS certificates (for Bradesco)
   * @param orgaoId - Organization ID
   * @param certificadoFormato - Certificate format: 'PEM_SEPARADO' or 'PKCS12'
   * @param certificadoPublico - Public certificate file (for PEM_SEPARADO)
   * @param chavePrivada - Private key file (for PEM_SEPARADO)
   * @param certificadoPkcs12 - PKCS12 certificate file (for PKCS12)
   * @param certificadoSenha - Certificate password (optional for PEM, required for PKCS12)
   * @returns {Observable<any>} - Observable containing the response
   */
  saveBankCertificates(
    orgaoId: number,
    certificadoFormato: 'PEM_SEPARADO' | 'PKCS12',
    certificadoPublico: File | null,
    chavePrivada: File | null,
    certificadoPkcs12: File | null,
    certificadoSenha: string
  ): Observable<any> {
    const formData = new FormData();
    formData.append('certificadoFormato', certificadoFormato);

    if (certificadoFormato === 'PEM_SEPARADO') {
      if (certificadoPublico) {
        formData.append('certificadoPublico', certificadoPublico, certificadoPublico.name);
      }
      if (chavePrivada) {
        formData.append('chavePrivada', chavePrivada, chavePrivada.name);
      }
      if (certificadoSenha) {
        formData.append('certificadoSenha', certificadoSenha);
      }
    } else if (certificadoFormato === 'PKCS12') {
      if (certificadoPkcs12) {
        formData.append('certificadoPkcs12', certificadoPkcs12, certificadoPkcs12.name);
      }
      if (certificadoSenha) {
        formData.append('certificadoSenha', certificadoSenha);
      }
    }

    return this.http.post(`${this.apiUrl}/orgaos/${orgaoId}/bank-certificates`, formData);
  }

  /**
   * Update inscricoesGratuitas configuration for an organization
   * PATCH /api/admin/orgaos/{orgaoId}/inscricoes-gratuitas
   * @param orgaoId - Organization ID
   * @param inscricoesGratuitas - Boolean value for free enrollments
   * @returns {Observable<Organization>} - Observable containing the updated organization data
   */
  updateInscricoesGratuitas(orgaoId: number, inscricoesGratuitas: boolean): Observable<Organization> {
    return this.http.patch<Organization>(
      `${this.apiUrl}/orgaos/${orgaoId}/inscricoes-gratuitas`,
      { inscricoesGratuitas }
    );
  }

  /**
   * Delete an organization
   * @param id - Organization ID
   * @returns {Observable<void>} - Observable for the delete operation
   */
  deleteOrganization(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/orgaos/${id}`);
  }
}
